/*
 * Purpose: Resolve mission request pages — AI-composed forms + generic gap fallback
 * Last modified: 2026-06-28 | Agent
 * Completeness: 98
 */
import type { MissionKnowledgeRequest } from '@/services/mission-knowledge/types';
import { knowledgeGapsFromList } from '@/lib/gapRemediation';
import {
  formatStructuredAnswersForContext,
  normalizeAiField,
  requestIsComplete,
} from '@/lib/missionRequestFields';

export { formatStructuredAnswersForContext, requestIsComplete };

function slugId(prefix: string, gap: string): string {
  return `${prefix}-${gap.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}`;
}

/** Universal fallback when the AI did not supply a custom form for a knowledge gap. */
function genericGapForm(gapRef: string): MissionKnowledgeRequest {
  return {
    id: slugId('gap', gapRef),
    gapRef,
    title: gapRef.length > 56 ? `${gapRef.slice(0, 53)}…` : gapRef,
    description: 'Details for this Mission — not shared with other Missions.',
    layout: 'form',
    fields: [
      {
        id: 'summary',
        label: 'Your answer',
        type: 'textarea',
        required: true,
        placeholder: 'Describe how this works for you in this Mission…',
      },
    ],
  };
}

function normalizeRequest(raw: MissionKnowledgeRequest): MissionKnowledgeRequest {
  return {
    ...raw,
    layout: raw.layout ?? 'form',
    fields: raw.fields.map((f) => normalizeAiField(f)).filter(Boolean) as MissionKnowledgeRequest['fields'],
  };
}

/**
 * Merge LLM knowledgeRequests with generic fallbacks for uncovered knowledge gaps.
 * Template is mission-agnostic — same field library for every Mission type.
 */
export function resolveKnowledgeRequests(input: {
  gaps: string[];
  rawOutcome: string;
  knowledgeRequests?: MissionKnowledgeRequest[];
}): MissionKnowledgeRequest[] {
  void input.rawOutcome;
  const knowledgeGaps = knowledgeGapsFromList(input.gaps);
  const fromAi = (input.knowledgeRequests ?? [])
    .map(normalizeRequest)
    .filter((r) => r.fields?.length > 0);

  const byKey = new Map<string, MissionKnowledgeRequest>();

  for (const req of fromAi) {
    byKey.set(req.id, req);
  }

  for (const gap of knowledgeGaps) {
    const gapKey = gap.toLowerCase();
    const hasGap = [...byKey.values()].some((r) => r.gapRef.toLowerCase() === gapKey);
    if (!hasGap) {
      byKey.set(slugId('gap', gap), genericGapForm(gap));
    }
  }

  return [...byKey.values()];
}
